import { useQuery } from '@tanstack/react-query';
import { type MessageKey, t } from '../../i18n';
import { api } from '../../lib/api';
import type { Priority } from './requests-data';

// Datos de /api/proyectos (guía 7.2).

export const PROJECT_STATUSES = ['planificacion', 'en_curso', 'en_pausa', 'terminado', 'cancelado'] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];
export type TaskCategory = 'por_hacer' | 'en_curso' | 'terminado' | 'cancelado';
export type ProjectScope = 'mios' | 'todos';

export interface ProjectListItem {
  id: string;
  number: number;
  code: string;
  name: string;
  organizationId: string;
  organization: string | null;
  projectType: string | null;
  status: ProjectStatus;
  priority: Priority;
  ownerId: string | null;
  owner: string | null;
  startDate: string | null;
  dueDate: string | null;
  lastActivityAt: string;
  progress: { done: number; total: number };
}

export interface ProjectTask {
  id: string;
  parentId: string | null;
  title: string;
  statusId: string;
  status: string;
  statusCategory: TaskCategory;
  priority: Priority;
  startDate: string | null;
  dueDate: string | null;
  sortOrder: number;
  completedAt: string | null;
  lastActivityAt: string;
  overdue: boolean;
  assignees: Array<{ id: string; fullName: string | null }>;
  blockedBy: string[];
}

export interface DocumentVersion {
  version: number;
  attachmentId: string;
  fileName: string;
  sizeBytes: number;
  note: string | null;
  createdBy: string;
  createdByName: string | null;
  createdAt: string;
}

export interface ProjectDocument {
  id: string;
  title: string;
  publishedToKbAt: string | null;
  versions: DocumentVersion[];
}

export interface Label {
  id: string;
  name: string;
  color: string;
}

export interface ProjectDetail {
  id: string;
  number: number;
  code: string;
  organizationId: string;
  organization: string | null;
  name: string;
  description: string | null;
  projectTypeId: string | null;
  projectType: string | null;
  /** No llega para quien solo sigue el proyecto desde su solicitud (guía 7.2). */
  priority?: Priority;
  status: ProjectStatus;
  ownerId: string | null;
  owner: string | null;
  startDate: string | null;
  dueDate: string | null;
  completedAt: string | null;
  lastActivityAt: string;
  request: { number: number; code: string } | null;
  labels: Label[];
  progress: { done: number; total: number };
  tasks: ProjectTask[];
  documents: ProjectDocument[];
  can: { manage: boolean; contribute: boolean };
}

export interface TaskStatusOption {
  id: string;
  name: string;
  category: TaskCategory;
  isInitial: boolean;
}

export interface ProjectFormData {
  organizations: Array<{ id: string; name: string; groupName: string }>;
  projectTypes: Array<{ id: string; name: string }>;
  projectLabels: Label[];
  taskStatuses: TaskStatusOption[];
  taskLabels: Label[];
}

export interface Person {
  id: string;
  fullName: string;
  email: string;
}

export const useProjectForm = () => useQuery({ queryKey: ['proyectos', 'formulario'], queryFn: () => api<ProjectFormData>('/api/proyectos/formulario'), staleTime: 60_000 });
export const useProject = (number: string, enabled = true) =>
  useQuery({ queryKey: ['proyectos', 'detalle', number], queryFn: () => api<ProjectDetail>(`/api/proyectos/${number}`), enabled: enabled && !!number });
export const usePeople = (organizationId: string | null | undefined) =>
  useQuery({
    queryKey: ['proyectos', 'personas', organizationId],
    queryFn: () => api<Person[]>(`/api/proyectos/personas?organizacion=${organizationId}`),
    enabled: !!organizationId,
    staleTime: 60_000,
  });

export const projectStatusName = (s: ProjectStatus) => t(`pr.estado_${s}` as MessageKey);
const PROJECT_TONES = { planificacion: 'slate', en_curso: 'blue', en_pausa: 'amber', terminado: 'slate', cancelado: 'slate' } as const;
export const projectStatusTone = (s: ProjectStatus) => PROJECT_TONES[s];
const TASK_TONES = { por_hacer: 'slate', en_curso: 'blue', terminado: 'slate', cancelado: 'slate' } as const;
export const taskTone = (c: TaskCategory) => TASK_TONES[c];

export const isOpen = (c: TaskCategory) => c === 'por_hacer' || c === 'en_curso';

/** Tareas en el orden del plan (fase, paso, subpaso), con su profundidad. */
export function planOrder<T extends { id: string; parentId: string | null; sortOrder: number }>(tasks: readonly T[]): Array<T & { depth: number }> {
  const children = new Map<string | null, T[]>();
  for (const t of tasks) children.set(t.parentId, [...(children.get(t.parentId) ?? []), t]);
  const out: Array<T & { depth: number }> = [];
  const visit = (parentId: string | null, depth: number) => {
    if (depth > 10) return;
    for (const t of [...(children.get(parentId) ?? [])].sort((a, b) => a.sortOrder - b.sortOrder)) {
      out.push({ ...t, depth });
      visit(t.id, depth + 1);
    }
  };
  visit(null, 0);
  return out;
}

/** Fecha corta (AAAA-MM-DD o ISO completa). */
export const formatDay = (iso: string) => new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso).toLocaleDateString('es-PA', { day: 'numeric', month: 'short', ...(iso.length === 10 ? { timeZone: 'UTC' } : {}) });

/** Hoy en la zona del navegador, como AAAA-MM-DD. */
export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// --- Vista Cancha (GET /api/proyectos/cancha) ---

export type Side = 'it' | 'negocio' | 'ambos' | 'sin_asignar' | 'sin_tareas';
export const SIDES: readonly Side[] = ['it', 'negocio', 'ambos', 'sin_asignar', 'sin_tareas'];

export interface CanchaRow {
  project: { id: string; number: number; code: string; name: string; organizationId: string; organization: string | null; status: ProjectStatus };
  current: {
    id: string;
    title: string;
    phase: string | null;
    status: string;
    statusCategory: TaskCategory;
    dueDate: string | null;
    overdue: boolean;
    lastActivityAt: string;
    assignees: Array<{ id: string; fullName: string; isIt: boolean }>;
  } | null;
  side: Side;
  lastActivityAt: string;
  idleBusinessDays: number;
  stale: boolean;
}

export interface CanchaBoard {
  idleDays: number;
  summary: Record<Side | 'stale', number>;
  projects: CanchaRow[];
}

export const sideName = (s: Side) => t(`ca.lado_${s}` as MessageKey);
const SIDE_STYLES = {
  it: 'bg-blue-100 text-blue-900',
  negocio: 'bg-amber-100 text-amber-900',
  ambos: 'bg-violet-100 text-violet-900',
  sin_asignar: 'bg-red-100 text-red-800',
  sin_tareas: 'bg-slate-100 text-slate-700',
} as const;
export const sideStyle = (s: Side) => SIDE_STYLES[s];
